import {
  DndContext,
  DragEndEvent,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  rectSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { FunctionComponent, PropsWithChildren, useCallback } from 'react';

export enum SortableMode {
  LIST = 'list',
  GRID = 'grid',
}

const SORTABLE_MODE_SETTINGS = {
  [SortableMode.LIST]: {
    strategy: verticalListSortingStrategy,
    modifiers: [restrictToVerticalAxis, restrictToParentElement],
  },
  [SortableMode.GRID]: {
    strategy: rectSortingStrategy,
    modifiers: [restrictToParentElement],
  },
};

export interface ReorderArgs {
  activeId: number;
  overId: number;
}

interface SortableListProps {
  items: number[];
  onReorder: (args: ReorderArgs) => void;
  mode?: SortableMode;
  disabled?: boolean;
}

export const SortableList: FunctionComponent<PropsWithChildren<SortableListProps>> = ({
  items,
  onReorder,
  mode = SortableMode.LIST,
  disabled,
  children,
}) => {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  const dragEndHandler = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over || over.id === active.id) {
        return;
      }

      onReorder({ activeId: Number(active.id), overId: Number(over.id) });
    },
    [onReorder]
  );

  const { strategy, modifiers } = SORTABLE_MODE_SETTINGS[mode];

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={modifiers}
      accessibility={{ container: document.body }}
      onDragEnd={dragEndHandler}
    >
      <SortableContext items={items} strategy={strategy} disabled={disabled}>
        {children}
      </SortableContext>
    </DndContext>
  );
};
